import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Select,
  Collapse,
  FormControl,
  debounce,
  InputAdornment,
  TextareaAutosize,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate, useParams } from "react-router-dom";
import TableDownloader from "../../TableDownloader";
import SearchBar from "../../SearchBar";
import { CiCirclePlus, CiFilter } from "react-icons/ci";
import { styled, alpha } from "@mui/material/styles";
import { InputBase } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import TargetPercentField from "../../utilisation/TargetPercentField";
import { DEFAULT_TARGET_PERCENT, validateTargetPercent } from "../../../utils/utilisation";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { IoIosArrowBack } from "react-icons/io";
import { Offcanvas } from "react-bootstrap";
import QuillComponent from "../../common/ReactQuill";
import { ExpandMore } from "@mui/icons-material";
import Comments from "./ProjectComments";
import StatusTimeline from "./StatusTimeline";
import ActivityStream from "./ProjectActivity";
import ProjectComments from "./ProjectComments";
import ProjectStatusTimeline from "./StatusTimeline";
import { FaUser } from "react-icons/fa";
import dayjs from "dayjs";
import { editProjectDetails, fetchEntityById } from "../../../apis/apiService";
import { useDispatch, useSelector } from "react-redux";
import { extractTextFromHTML } from "../../../utils/textUtils";
import { validateOptionalPositive } from "../../../utils/amount";
import { sameValue, useAutoSave } from "../../../hooks/useAutoSave";
import { toast } from "react-toastify";
import Loader from "../../common/Loader";
import { setTasklistDatas } from "../../../selectSlice";
import {
  useFetchNotificationListQuery,
  useFetchProjectActivityQuery,
  useFetchViewProjectsByIdQuery,
  useFetchInvoicesQueueQuery,
} from "../../../slice/logdetailSlice";
import {
  preventNegativeValues,
  validateEndDate,
  validateStartDate,
  parseDate,
} from "../../common/PreventValidate";

// import {fetchEntityById} from ''
const SubProjectTabs = ({ projectDetail, comment_count }) => {
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  // //console.log()("login_user in project:", login_user);
  const { role: login_user_role } = login_user || {};
  const isAdminOrManager =
    login_user_role === "ADMIN" || login_user_role === "MANAGER";
  const canViewSubTabs =
    isAdminOrManager || login_user_role === "EMPLOYEE";

  return (
    <>
      {canViewSubTabs && (
        <div>
          <ul className="nav nav-tabs" id="myTab" role="tablist">
            <li className="nav-item pe-4" role="presentation">
              <button
                className="active text-rgba-gray R_btn"
                id="Comments-tab"
                data-bs-toggle="tab"
                data-bs-target="#Comments"
                type="button"
                role="tab"
                aria-controls="Comments"
                aria-selected="true"
              >
                Comments ({comment_count})
              </button>
            </li>
            {isAdminOrManager && (
              <>
                <li className="nav-item pe-4" role="presentation">
                  <button
                    className="text-rgba-gray R_btn"
                    id="StatusTimeline-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#StatusTimeline"
                    type="button"
                    role="tab"
                    aria-controls="StatusTimeline"
                    aria-selected="false"
                  >
                    Status Timeline
                  </button>
                </li>
                <li className="nav-item pe-4" role="presentation">
                  <button
                    className="text-rgba-gray R_btn"
                    id="ActivityStream-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#ActivityStream"
                    type="button"
                    role="tab"
                    aria-controls="ActivityStream"
                    aria-selected="false"
                  >
                    Activity Stream
                  </button>
                </li>
              </>
            )}
          </ul>

          <div className="tab-content" id="myTabContent">
            <div
              className="tab-pane fade show active"
              id="Comments"
              role="tabpanel"
              aria-labelledby="Comments-tab"
            >
              <ProjectComments projectDetail={projectDetail} />
            </div>
            {isAdminOrManager && (
              <>
                <div
                  className="tab-pane fade"
                  id="StatusTimeline"
                  role="tabpanel"
                  aria-labelledby="StatusTimeline-tab"
                >
                  <ProjectStatusTimeline />
                </div>
                <div
                  className="tab-pane fade"
                  id="ActivityStream"
                  role="tabpanel"
                  aria-labelledby="ActivityStream-tab"
                >
                  <ActivityStream />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
// 0 is not a valid Target % (it must be 1-100): projects created before the column had a
// default carry 0, which means "not set yet" and is shown as the default.
const storedTargetPercent = (value) => (Number(value) > 0 ? value : DEFAULT_TARGET_PERCENT);

// Budget fields that are saved as soon as the user leaves them. When filled in they must be
// above zero; emptying one is still allowed (it clears the value).
const BUDGET_FIELD_LABELS = {
  budgeted_hours: "Budgeted Hours",
  total_budget: "Total Budget",
};

// Fields of the detail page that are saved as the user edits them.
const AUTOSAVE_FIELDS = [
  "description",
  "project_manager_id",
  "current_status_id",
  "budget_type",
  "budgeted_hours",
  "total_budget",
  "completed_percentage",
  "start_date",
  "end_date",
];

// The description is stored as plain text once edited, but may still arrive from the server as
// HTML. Only strip tags when there are some, otherwise typed spaces and line breaks would be
// trimmed away while the user is typing.
const HTML_TAG = /<[^>]+>/;
const descriptionText = (value) =>
  HTML_TAG.test(value ?? "") ? extractTextFromHTML(value) : value ?? "";

const comparableValue = (name, value) =>
  name === "description" ? descriptionText(value) : value;

// After the project is fetched again (e.g. right after a save), take the server's values but keep
// anything the user has typed since the last save, so typing is never overwritten.
const mergeServerProject = (server, local, baseline) => {
  if (!baseline || baseline.project_id !== server.project_id) return server;
  const merged = { ...server };
  AUTOSAVE_FIELDS.forEach((name) => {
    if (
      name in local &&
      !sameValue(comparableValue(name, local[name]), comparableValue(name, baseline[name]))
    ) {
      merged[name] = local[name];
    }
  });
  return merged;
};

const ProjectTab = ({ projectDetail }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [selectedUser, setSelectedUser] = useState("");
  const [dateErrors, setDateErrors] = useState({ start_date: "", end_date: "" });
  const start_date_timer = useRef(null);
  const end_date_timer = useRef(null);
  const [loading, setLoading] = useState(false);
  const { data, refetch: project_activity } = useFetchProjectActivityQuery(id);
  const { data: notification_list, refetch: notification_refetch } =
    useFetchNotificationListQuery();
  const [projects, setProjects] = useState({
    description: "",
    project_manager_id: null,
    current_status_id: null,
    start_date: null,
    end_date: null,
    currency: "INR",
    budget_type: null,
    budgeted_hours: null,
    total_budget: null,
    completed_percentage: 0,
  });
  // Errors only show once the user has touched the field, so a project that already has an
  // empty or zero budget does not open with red fields.
  // What the server has, per field - used to tell a real change from an untouched field.
  const savedValuesRef = useRef(null);
  const isUnsaved = (name, value) => {
    const saved = savedValuesRef.current;
    if (!saved || !(name in saved)) return true;
    return !sameValue(comparableValue(name, saved[name]), comparableValue(name, value));
  };
  // Saves a field one second after its last change (see persistField, defined further down).
  const autoSave = useAutoSave((name, value) => persistField(name, value));
  const [touchedBudgetFields, setTouchedBudgetFields] = useState({});
  const getBudgetFieldError = (name) =>
    touchedBudgetFields[name]
      ? validateOptionalPositive(projects[name], BUDGET_FIELD_LABELS[name])
      : null;
  //const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  //console.log("login_user in project:", login_user);
  //console.log("login_user role in project:", login_user_role);
  const {
    data: project_detail,
    isLoading,
    refetch,
  } = useFetchViewProjectsByIdQuery(id, {
    refetchOnMountOrArgChange: true,
  });

  const invoicesQueryResult = useFetchInvoicesQueueQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const invoicesData = invoicesQueryResult?.data;

  // Check if project has invoices with Mapped or Allocated status in Resource Earning
  const hasMappedOrAllocatedInvoices = useMemo(() => {
    if (project_detail?.is_budget_type_locked || project_detail?.has_mapped_invoices) {
      return true;
    }
    if (Array.isArray(project_detail?.invoices) && project_detail.invoices.some((inv) => {
      const s = String(inv.status || "").toLowerCase();
      return s === "mapped" || s === "allocated";
    })) {
      return true;
    }

    const invoicesList = Array.isArray(invoicesData?.data) ? invoicesData.data : [];
    if (invoicesList.length > 0 && (id || project_detail?.project_id)) {
      const projIdStr = String(id || project_detail?.project_id);
      return invoicesList.some((inv) => {
        const matchesProject =
          String(inv.project_id) === projIdStr ||
          (project_detail?.title && (inv.project_name === project_detail.title || inv.project_title === project_detail.title)) ||
          (project_detail?.project_name && (inv.project_name === project_detail.project_name || inv.project_title === project_detail.project_name));
        const status = String(inv.status || "").toLowerCase();
        return matchesProject && (status === "mapped" || status === "allocated");
      });
    }

    return false;
  }, [project_detail, invoicesData, id]);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (start_date_timer.current) clearTimeout(start_date_timer.current);
      if (end_date_timer.current) clearTimeout(end_date_timer.current);
    };
  }, []);

  const safeRefetch = (refetchFn) => {
    if (isMounted.current && typeof refetchFn === "function") {
      try {
        refetchFn();
      } catch (err) {
        console.warn("Refetch safely ignored on unmounted query:", err);
      }
    }
  };

  useEffect(() => {
    safeRefetch(refetch);
  }, [id]);

  useEffect(() => {
    if (project_detail) {
      // const { project_id } = project_detail;
      const baseline = savedValuesRef.current;
      const isOtherProject = baseline?.project_id !== project_detail.project_id;
      const server = { ...project_detail, target_percent: storedTargetPercent(project_detail.target_percent) };
      setProjects((prev) => mergeServerProject(server, prev, isOtherProject ? null : baseline));
      if (isOtherProject) setTouchedBudgetFields({});
      savedValuesRef.current = project_detail;
      // const project_name = project_detail.project_name;
      // localStorage.setItem("project_id", project_id);
      // setProject(project_detail);
      // localStorage.setItem("project_name", project_name);
      // fetchProjectUsers();
    }
  }, [project_detail]);
  const usersInProjects = useSelector(
    (state) => state.selectOptions?.ownerNameOptions
  );

  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  // //console.log()("login_user_detail in redux state: ", login_user);
  const { role: login_user_role } = login_user;
  const { name, id: login_user_id } = login_user;
  // const login_user_id = id;
  // const login_user_name = name;
  // //console.log()("login_user_detail :id", login_user_id);
  useEffect(() => {
    if (usersInProjects && login_user_id) {
      const matchingUser = usersInProjects.find(
        (user) => user.user_id === login_user_id
      );
      // //console.log()("login_user_detail matching value :", matchingUser);

      if (matchingUser) {
        setSelectedUser(matchingUser.user_id); // Set the default value
      }
    }
  }, [usersInProjects, login_user_id]);
  const StatusOptions = useSelector(
    (state) => state.selectOptions?.StatusOptions
  );
  const statusNames = StatusOptions.find(
    (status) => status.status_type === "PROJECT"
  );
  const status_list = statusNames?.status_details?.map((status) => ({
    status_id: status.status_id,
    status_name: status.status_name,
    status_colour: status.status_colour,
  }));
  const UserNameOptions = useSelector(
    (state) => state.selectOptions?.ownerNameOptions
  );
  const completedPercentage = [0, 25, 50, 75, 100];
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {}, // Initially empty
  });
  const formValues = watch();
  const previousValues = useRef(formValues);
  const fetchDataOnChange = async (updatedData) => {
    if (!projectDetail?.project_id) {
      console.error("project_id is missing.");
      return;
    }
    if (isMounted.current) {
      setLoading(true);
    }
    try {
      const response = await editProjectDetails(
        "projects/update",
        projectDetail?.project_id,
        updatedData
      );
      safeRefetch(notification_refetch);
      if (response?.message) {
        toast.success(response.message);
      }
    } catch (error) {
      if (
        error?.message?.includes("Cannot refetch a query") ||
        error?.message?.includes("error #38")
      ) {
        console.warn("Suppressed RTK refetch error on unmount:", error);
      } else {
        toast.error(error?.message || "Failed to update project details");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };
  const isFirstRender = useRef(true);
  const isEditable =
    login_user_role === "ADMIN" || login_user_role === "MANAGER";
  // The project's Target %: what the server has (70 when none is stored yet), or what is being typed.
  const targetPercent = projects.target_percent ?? DEFAULT_TARGET_PERCENT;
  // Saved when the user leaves the field, like the others, but never an invalid value
  // (the message stays under the field) and not when it is unchanged.
  const handleTargetPercentBlur = (event) => {
    const { value } = event.target;
    if (validateTargetPercent(value)) return;
    const stored = storedTargetPercent(project_detail?.target_percent);
    if (Number(value) === Number(stored)) return;
    handleBlur({ target: { name: "target_percent", value: Number(value) } });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // Skip first render
    }

    const hasChanges = Object.keys(formValues).some(
      (key) => formValues[key] !== previousValues.current[key]
    );

    if (hasChanges) {
      const start_date = dayjs(projects.start_date);
      const end_date = dayjs(projects.end_date);
      // //console.log()("Form data changed, making API call", hasChanges);
      fetchDataOnChange(formValues);
      previousValues.current = formValues;
    }
  }, [formValues]);

  const [budgetOpen, setBudgetOpen] = useState(true);
  const [selectedBudgetType, setSelectedBudgetType] = useState("");

  // if (!projectDetail) {
  //   <Loader />;
  // }
  const handleNameChange = (e) => {
    const { name, value } = e.target;
    if (name === "budget_type" && hasMappedOrAllocatedInvoices) {
      toast.warning("Budget Type cannot be changed because this project has invoices in Mapped or Allocated status in Resource Earning.");
      return;
    }
    setProjects((prev) => ({ ...prev, [name]: value }));
    if (BUDGET_FIELD_LABELS[name]) {
      setTouchedBudgetFields((prev) => ({ ...prev, [name]: true }));
    }
    if (!AUTOSAVE_FIELDS.includes(name)) return;

    // Save once the user has paused for a second. Nothing is saved for a value that is invalid
    // (a zero budget) or that is back to what is already stored.
    const isInvalid =
      BUDGET_FIELD_LABELS[name] && validateOptionalPositive(value, BUDGET_FIELD_LABELS[name]);
    if (isInvalid || !isUnsaved(name, value)) {
      autoSave.cancel(name);
    } else {
      autoSave.schedule(name, value);
    }
  };
  // Leaving a field saves a pending change straight away, and does nothing when nothing changed.
  const handleBlur = async (e) => {
    const { name, value } = e.target;
    autoSave.cancel(name);
    if (BUDGET_FIELD_LABELS[name]) {
      setTouchedBudgetFields((prev) => ({ ...prev, [name]: true }));
    }
    await persistField(name, value);
  };
  const persistField = async (name, value) => {
    // A zero budget is never saved. The error stays under the field until it is corrected.
    if (BUDGET_FIELD_LABELS[name] && validateOptionalPositive(value, BUDGET_FIELD_LABELS[name])) {
      return;
    }
    if (!isUnsaved(name, value)) return;
    // // console.log(" name_change handleBlur :", name, value);
    // //console.log(" name_change project value :", projects);
    const requestedData = {
      [name]: value,
    };
    try {
      const response = await editProjectDetails(
        "projects/update",
        projectDetail?.project_id,
        // projects
        requestedData
      );
      savedValuesRef.current = { ...(savedValuesRef.current || {}), [name]: value };
      safeRefetch(project_activity);
      safeRefetch(refetch);
      if (response?.message) {
        toast.success(response.message);
      }
    } catch (error) {
      if (
        error?.message?.includes("Cannot refetch a query") ||
        error?.message?.includes("error #38")
      ) {
        console.warn("Suppressed RTK refetch error on unmount:", error);
      } else {
        toast.error(error?.message || "Failed to update project details");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const BudgetOptions = [
    { value: "FIXED_COST", label: "Fixed Cost" },
    { value: "TIME_AND_MATERIAL", label: "Time & Material" },
    { value: "CONTRACT", label: "Contract" },
    // { value: "NONE", label: "None" },
    // { value: "Based on Project", label: "Based on Project" },
  ];
  // //console.log()("projectss_id :", projects.project_manager_id);

  const onSubmit = (data) => {};

  return (
    <>
      <div className="py-3">
        {loading ? (
          <Loader /> // Display loader while loading
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div className="row">
                  <div className="col-md-12 pb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <TextareaAutosize
                      name="description"
                      onBlur={handleBlur}
                      minRows={4}
                      maxRows={6}
                      onChange={handleNameChange}
                      value={descriptionText(projects.description)}
                      placeholder="Enter description"
                      className="textAreaField"
                      disabled={!isEditable}
                    />
                  </div>
                  <div className="col-md-3 pb-3">
                    <label htmlFor="project_manager_id" className="form-label">
                      Owner
                    </label>
                    <FormControl fullWidth>
                      <Select
                        // {...field}
                        // value={field.value || ""} // Ensure it doesn't break if undefined
                        name="project_manager_id"
                        IconComponent={ExpandMoreIcon}
                        value={projects.project_manager_id ?? ""}
                        onChange={handleNameChange}
                        onBlur={handleBlur}
                        displayEmpty
                        disabled={!isEditable}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span className="lightText">Owner</span>;
                          }
                          const selectedUserObj = usersInProjects.find(
                            (user) => user.user_id === selected
                          );
                          return selectedUserObj ? (
                            selectedUserObj.user_id === login_user_id ? (
                              `${selectedUserObj.name} (You)`
                            ) : (
                              selectedUserObj.name
                            )
                          ) : (
                            <span className="lightText">Owner</span>
                          );
                        }}
                      >
                        {usersInProjects.map((userlist, index) => {
                          if (userlist.user_id === login_user_id) {
                            // //console.log()(
                            //   "login_user_detail user ids are same...😇"
                            // );
                          }
                          return (
                            <MenuItem
                              key={userlist.user_id}
                              index={index}
                              defaultValue={userlist.user_id === login_user_id}
                              value={userlist.user_id}
                            >
                              {userlist.user_id === login_user_id
                                ? `${userlist.name} (You)`
                                : userlist.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="col-md-3 pb-3">
                    <label htmlFor="current_status" className="form-label">
                      Status
                    </label>
                    <FormControl fullWidth>
                      <Select
                        name="current_status_id"
                        onBlur={handleBlur}
                        onChange={handleNameChange}
                        // {...field}
                        value={projects.current_status_id || ""}
                        IconComponent={ExpandMoreIcon}
                        displayEmpty
                        disabled={!isEditable}
                        renderValue={(selected) => {
                          const selected_value = status_list?.find(
                            (status) => status.status_id === selected
                          );
                          // //console.log()("selected_value :", selected_value);

                          return selected_value ? (
                            <span
                              style={{ color: selected_value.status_colour }}
                            >
                              {selected_value.status_name}
                            </span>
                          ) : (
                            <span className=" lightText">Select status</span>
                          );
                        }}
                      >
                        {status_list?.map((status) => (
                          <MenuItem
                            key={status.status_id}
                            value={status.status_id}
                            sx={{ color: status.status_colour || "black" }}
                          >
                            {status.status_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label htmlFor="start_date" className="form-label">
                      Start Date
                    </label>
                    <div className="d-flex  flex-column w-100">
                      <DatePicker
                        className="datepicker_input"
                        name="start_date"
                        format="DD/MM/YYYY"
                        disabled={!isEditable}
                        value={
                          projects.start_date
                            ? dayjs(projects.start_date)
                            : null
                        }
                        onChange={(date) => {
                          const isValidFormat = !date || (dayjs(date).isValid() && dayjs(date).year() >= 1900);
                          
                          let isOrderValid = true;
                          if (isValidFormat && date) {
                            const newDateFormatted = dayjs(date).format("YYYY-MM-DD");
                            if (projects.end_date && !validateStartDate(newDateFormatted, projects.end_date)) {
                              isOrderValid = false;
                            }
                          }

                          const newDate = date
                            ? (dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : null)
                            : null;
                          //console.log()("onchange_date new date:", newDate);

                          setProjects((prev) => ({
                            ...prev,
                            start_date: newDate,
                          }));

                          if (isValidFormat && isOrderValid) {
                            if (start_date_timer.current) {
                              clearTimeout(start_date_timer.current);
                            }
                            setDateErrors((prev) => ({
                              ...prev,
                              start_date: "",
                            }));
                            handleBlur({
                              target: {
                                name: "start_date",
                                value: newDate,
                              },
                            });
                          } else {
                            const year = date ? dayjs(date).year() : 0;
                            const isCompleteInvalidYear = year > 1000 && year < 1900;
                            const isInvalid = !isValidFormat || !isOrderValid;

                            if (isInvalid) {
                              if (start_date_timer.current) {
                                clearTimeout(start_date_timer.current);
                              }

                              start_date_timer.current = setTimeout(() => {
                                setProjects((prev) => ({
                                  ...prev,
                                  start_date: project_detail?.start_date,
                                }));
                                setDateErrors((prev) => ({
                                  ...prev,
                                  start_date: "",
                                }));
                              }, 3000);

                              if (isCompleteInvalidYear || !isOrderValid || !dayjs(date).isValid()) {
                                setDateErrors((prev) => ({
                                  ...prev,
                                  start_date: "Invalid date format",
                                }));
                              }
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            className="form-control datepicker_input"
                            {...params}
                            onBlur={(e) => {
                              const rawVal = e.target.value;
                              if (rawVal && rawVal.trim() !== "") {
                                const d = parseDate(rawVal);
                                
                                let isOrderValid = true;
                                if (d && d.isValid() && projects.end_date) {
                                  const end = parseDate(projects.end_date);
                                  if (end && end.isValid() && d.isAfter(end, "day")) {
                                    isOrderValid = false;
                                  }
                                }

                                if (!d || !d.isValid() || d.year() < 1900 || !isOrderValid) {
                                  setDateErrors((prev) => ({
                                    ...prev,
                                    start_date: "Invalid date format",
                                  }));

                                  if (start_date_timer.current) {
                                    clearTimeout(start_date_timer.current);
                                  }

                                  start_date_timer.current = setTimeout(() => {
                                    setProjects((prev) => ({
                                      ...prev,
                                      start_date: project_detail?.start_date,
                                    }));
                                    setDateErrors((prev) => ({
                                      ...prev,
                                      start_date: "",
                                    }));
                                  }, 3000);
                                }
                              }
                            }}
                          />
                        )}
                      />
                      {dateErrors.start_date && (
                        <span className="errorMsg">
                          {dateErrors.start_date}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label htmlFor="end_date" className="form-label">
                      End Date
                    </label>
                    <div className="d-flex  flex-column w-100">
                      <DatePicker
                        className="datepicker_input"
                        name="end_date"
                        value={
                          projects.end_date ? dayjs(projects.end_date) : null
                        }
                        disabled={!isEditable}
                        format="DD/MM/YYYY"
                        onChange={(date) => {
                          const isValidFormat = !date || (dayjs(date).isValid() && dayjs(date).year() >= 1900);
                          
                          let isOrderValid = true;
                          if (isValidFormat && date) {
                            const newDateFormatted = dayjs(date).format("YYYY-MM-DD");
                            if (projects.start_date && !validateEndDate(projects.start_date, newDateFormatted)) {
                              isOrderValid = false;
                            }
                          }

                          const newDate = date
                            ? (dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : null)
                            : null;

                          setProjects((prev) => ({
                            ...prev,
                            end_date: newDate,
                          }));

                          if (isValidFormat && isOrderValid) {
                            if (end_date_timer.current) {
                              clearTimeout(end_date_timer.current);
                            }
                            setDateErrors((prev) => ({
                              ...prev,
                              end_date: "",
                            }));
                            handleBlur({
                              target: {
                                name: "end_date",
                                value: newDate,
                              },
                            });
                          } else {
                            const year = date ? dayjs(date).year() : 0;
                            const isCompleteInvalidYear = year > 1000 && year < 1900;
                            const isInvalid = !isValidFormat || !isOrderValid;

                            if (isInvalid) {
                              if (end_date_timer.current) {
                                  clearTimeout(end_date_timer.current);
                              }

                              end_date_timer.current = setTimeout(() => {
                                setProjects((prev) => ({
                                  ...prev,
                                  end_date: project_detail?.end_date,
                                }));
                                setDateErrors((prev) => ({
                                  ...prev,
                                  end_date: "",
                                }));
                              }, 3000);

                              if (isCompleteInvalidYear || !isOrderValid || !dayjs(date).isValid()) {
                                setDateErrors((prev) => ({
                                  ...prev,
                                  end_date: "Invalid date format",
                                }));
                              }
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            className="form-control datepicker_input"
                            {...params}
                            onBlur={(e) => {
                              const rawVal = e.target.value;
                              if (rawVal && rawVal.trim() !== "") {
                                const d = parseDate(rawVal);
                                
                                let isOrderValid = true;
                                if (d && d.isValid() && projects.start_date) {
                                  const start = parseDate(projects.start_date);
                                  if (start && start.isValid() && d.isBefore(start, "day")) {
                                    isOrderValid = false;
                                  }
                                }

                                if (!d || !d.isValid() || d.year() < 1900 || !isOrderValid) {
                                  setDateErrors((prev) => ({
                                    ...prev,
                                    end_date: "Invalid date format",
                                  }));

                                  if (end_date_timer.current) {
                                    clearTimeout(end_date_timer.current);
                                  }

                                  end_date_timer.current = setTimeout(() => {
                                    setProjects((prev) => ({
                                      ...prev,
                                      end_date: project_detail?.end_date,
                                    }));
                                    setDateErrors((prev) => ({
                                      ...prev,
                                      end_date: "",
                                    }));
                                  }, 3000);
                                }
                              }
                            }}
                          />
                        )}
                      />
                      {dateErrors.end_date && (
                        <span className="errorMsg">
                          {dateErrors.end_date}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {(login_user_role === "ADMIN" ||
                  login_user_role === "MANAGER") && (
                  <>
                    <button
                      type="button"
                      className="budget-toggle-btn my-4"
                      onClick={() => setBudgetOpen((open) => !open)}
                    >
                      BUDGET
                      {budgetOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </button>

                    {/* Budget Fields - Collapsible Section */}
                    <Collapse timeout={700} in={budgetOpen}>
                      <div className="row">
                        <div className="col-md-3 pb-3">
                          <label htmlFor="currency" className="form-label">
                            Currency
                          </label>
                          <Controller
                            name="currency"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                // disabled={!isEditable}
                                value={projects.currency}
                                onChange={handleNameChange}
                                onBlur={handleBlur}
                                fullWidth
                                disabled
                                placeholder="Currency"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-3 pb-3">
                          <label htmlFor="budget_type" className="form-label">
                            Budget Type
                          </label>
                          <Tooltip
                            title={
                              hasMappedOrAllocatedInvoices
                                ? "Budget Type cannot be changed because this project has invoices in Mapped or Allocated status in Resource Earning."
                                : ""
                            }
                            arrow
                            placement="top"
                            disableHoverListener={!hasMappedOrAllocatedInvoices}
                          >
                            <span style={{ display: "block", cursor: hasMappedOrAllocatedInvoices ? "not-allowed" : "default" }}>
                              <FormControl fullWidth>
                                <Select
                                  name="budget_type"
                                  value={projects.budget_type ?? ""}
                                  onChange={handleNameChange}
                                  onBlur={handleBlur}
                                  IconComponent={ExpandMore}
                                  displayEmpty
                                  disabled={!isEditable || hasMappedOrAllocatedInvoices}
                                  renderValue={(selected) =>
                                    selected ? (
                                      BudgetOptions?.find(
                                        (budget_type) => budget_type.value === selected
                                      )?.label
                                    ) : (
                                      <span className=" lightText">Budget</span>
                                    )
                                  }
                                >
                                  {BudgetOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </span>
                          </Tooltip>
                        </div>
                        <div className="col-md-3 pb-3">
                          <label htmlFor="budgeted_hours" className="form-label">
                            Budgeted Hours
                          </label>
                          <FormControl fullWidth>
                            <Controller
                              name="budgeted_hours"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  id="budgeted_hours"
                                  disabled={!isEditable}
                                  {...field}
                                  value={projects.budgeted_hours}
                                  onChange={(event) => {
                                    preventNegativeValues(event);
                                    handleNameChange(event);
                                  }}
                                  onBlur={handleBlur}
                                  type="number"
                                  inputProps={{ min: 1 }}
                                  placeholder="Hours"
                                  error={Boolean(getBudgetFieldError("budgeted_hours"))}
                                  helperText={getBudgetFieldError("budgeted_hours")}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                        <div className="col-md-3 pb-3">
                          <label htmlFor="total_budget" className="form-label">
                            Total Budget
                          </label>
                          <FormControl fullWidth>
                            <Controller
                              name="total_budget"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  id="total_budget"
                                  {...field}
                                  disabled={!isEditable}
                                  value={projects.total_budget}
                                  onChange={(event) => {
                                    preventNegativeValues(event); // Ensure no negative input
                                    handleNameChange(event);
                                  }}
                                  onBlur={handleBlur}
                                  type="number"
                                  inputProps={{ min: 1 }}
                                  placeholder="Budget"
                                  error={Boolean(getBudgetFieldError("total_budget"))}
                                  helperText={getBudgetFieldError("total_budget")}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                        {projects.budget_type === "FIXED_COST" && (
                          <div className="col-md-3 pb-3">
                            <TargetPercentField
                              mode="edit"
                              id="target_percent"
                              budget={projects.total_budget}
                              value={targetPercent}
                              onChange={handleNameChange}
                              onBlur={handleTargetPercentBlur}
                              disabled={!isEditable}
                              showError
                            />
                          </div>
                        )}
                        <div className="col-md-3 pb-3">
                          <label
                            htmlFor="completed_percentage"
                            className="form-label"
                          >
                            Completed Percentage
                          </label>
                          <FormControl fullWidth>
                            <Select
                              name="completed_percentage"
                              value={projects.completed_percentage ?? ""}
                              disabled={!isEditable}
                              onChange={handleNameChange}
                              onBlur={handleBlur}
                              IconComponent={ExpandMore}
                              displayEmpty
                              renderValue={(selected) =>
                                selected || selected === 0 ? (
                                  `${selected}%`
                                ) : (
                                  <span className=" lightText">Percentage</span>
                                )
                              }
                            >
                              {completedPercentage.map((option, index) => (
                                <MenuItem key={index} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </Collapse>
                  </>
                )}
              </div>
            </form>
          </LocalizationProvider>
        )}
        <SubProjectTabs
          projectDetail={projectDetail}
          comment_count={projects.comment_count}
        />
      </div>
    </>
  );
};

export default ProjectTab;
